import {
  ActionMenu,
  ConfirmationDialog,
  useNotifications
} from '@ijoin/components';
import {
  GetAdminPlansRes,
  useDeletePlan,
  usePatchAdminPlanStatus,
  usePatchPlan,
  usePostAdminParticipantLiteSync,
  usePostAdminPlanReliusParticipantSync,
  usePostAdminPlanReliusPlanSync
} from '@ijoin/hooks';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { FC, useState } from 'react';
import { useToggle } from 'usehooks-ts';

const PLAN_STATUS = {
  ARCHIVE: 'ea6ff457-3b8c-4c70-ba70-566b96178c87',
  DELETING: 'a1b2c3d4-e5f6-4a5b-8c7d-9e0f1a2b3c4d',
  LIVE: '5c1a4b9b-f33e-4038-bb86-b0710ae74887'
};

export const PlanActionsCell: FC<{
  advisoryOrganizationId: string;
  row: GetAdminPlansRes;
}> = props => {
  const [isDeleteOpen, toggleDeleteOpen] = useToggle();
  const [isStatusOpen, toggleStatusOpen] = useToggle();
  const [isToggleActOpen, toggleActOpen] = useToggle();
  const [pendingSyncKey, setPendingSyncKey] = useState<null | string>(null);
  const notifications = useNotifications();
  const queryClient = useQueryClient();

  const invalidatePlans = () => {
    queryClient.refetchQueries({
      queryKey: [
        `/api/v3/admin/advisory-organizations/${props.advisoryOrganizationId}/plans`
      ]
    });
  };

  const patchStatus = usePatchAdminPlanStatus({
    mutation: {
      onError: () => notifications.error('Failed to update plan status'),
      onSuccess: () => {
        invalidatePlans();
        notifications.success('Plan status updated');
      }
    }
  });

  const patchPlan = usePatchPlan({
    mutation: {
      onError: () => notifications.error('Failed to update plan'),
      onSuccess: () => {
        invalidatePlans();
        notifications.success('Plan updated');
      }
    }
  });

  const deletePlan = useDeletePlan({
    mutation: {
      onError: () => notifications.error('Failed to delete plan'),
      onSuccess: () => {
        invalidatePlans();
        notifications.success('Plan deletion initiated');
      }
    }
  });

  const planSync = usePostAdminPlanReliusPlanSync({
    mutation: {
      onError: () => notifications.error('Failed to trigger plan sync'),
      onSuccess: () => notifications.success('Plan sync triggered successfully')
    }
  });

  const participantSync = usePostAdminPlanReliusParticipantSync({
    mutation: {
      onError: () => notifications.error('Failed to trigger participant sync'),
      onSuccess: () =>
        notifications.success('Participant sync triggered successfully')
    }
  });

  const participantLiteSync = usePostAdminParticipantLiteSync({
    mutation: {
      onError: () =>
        notifications.error('Failed to trigger participant lite sync'),
      onSuccess: () =>
        notifications.success('Participant lite sync triggered successfully')
    }
  });

  const syncs = [
    {
      key: 'planSync',
      label: 'Plan Sync',
      message: `This will import the plan configuration for "${props.row.description}" from Relius.`,
      mutation: planSync
    },
    {
      key: 'participantSync',
      label: 'Participant Sync',
      message: `This will run a full participant sync from Relius for all participants in "${props.row.description}".`,
      mutation: participantSync
    },
    {
      key: 'participantLiteSync',
      label: 'Participant Lite Sync',
      message: `This will trigger a participant lite sync for all participants in "${props.row.description}".`,
      mutation: participantLiteSync
    }
  ];

  const pendingSync = syncs.find(sync => sync.key === pendingSyncKey);

  const onCancelSync = () => setPendingSyncKey(null);

  const onConfirmSync = () => {
    pendingSync?.mutation.mutate({ planId: props.row.planId });
    setPendingSyncKey(null);
  };

  const isArchived = props.row.planStatusId === PLAN_STATUS.ARCHIVE;

  return (
    <>
      <ActionMenu
        actions={[
          {
            content: props.row.isAnalyticPortalAct
              ? 'Disable ACT'
              : 'Enable ACT',
            onSelect: toggleActOpen
          },
          {
            content: isArchived ? 'Reactivate Plan' : 'Archive Plan',
            onSelect: toggleStatusOpen
          },
          {
            content: 'Delete Plan',
            onSelect: toggleDeleteOpen
          },
          ...(props.row.isRelius
            ? syncs.map(sync => ({
                content: sync.label,
                onSelect: () => setPendingSyncKey(sync.key)
              }))
            : [])
        ]
          .filter(action => !!action)
          .sort((a, b) => a.content.localeCompare(b.content))}
        trigger={
          <IconButton data-testid={`planActions-${props.row.planId}`}>
            <MoreVertIcon />
          </IconButton>
        }
      />
      <ConfirmationDialog
        confirmText={props.row.isAnalyticPortalAct ? 'Disable' : 'Enable'}
        data-testid={`toggleAct-${props.row.planId}`}
        isOpen={isToggleActOpen}
        message={
          props.row.isAnalyticPortalAct
            ? `This will disable ACT Analytics on "${props.row.description}".`
            : `This will enable ACT Analytics on "${props.row.description}".`
        }
        onCancel={toggleActOpen}
        onConfirm={() => {
          toggleActOpen();
          patchPlan.mutate({
            data: { isAnalyticPortalAct: !props.row.isAnalyticPortalAct },
            planId: props.row.planId
          });
        }}
        title={props.row.isAnalyticPortalAct ? 'Disable ACT' : 'Enable ACT'}
      />
      {isArchived ? (
        <ConfirmationDialog
          confirmText='Reactivate'
          data-testid={`reactivatePlan-${props.row.planId}`}
          isOpen={isStatusOpen}
          message={`This will set "${props.row.description}" back to Live status.`}
          onCancel={toggleStatusOpen}
          onConfirm={() => {
            toggleStatusOpen();
            patchStatus.mutate({
              data: { planStatusId: PLAN_STATUS.LIVE },
              planId: props.row.planId
            });
          }}
          title='Reactivate Plan'
        />
      ) : (
        <ConfirmationDialog
          confirmColor='error'
          confirmText='Archive'
          data-testid={`archivePlan-${props.row.planId}`}
          isOpen={isStatusOpen}
          message={`This will archive "${props.row.description}". The plan will no longer be active.`}
          onCancel={toggleStatusOpen}
          onConfirm={() => {
            toggleStatusOpen();
            patchStatus.mutate({
              data: { planStatusId: PLAN_STATUS.ARCHIVE },
              planId: props.row.planId
            });
          }}
          title='Archive Plan'
        />
      )}
      <ConfirmationDialog
        confirmationText='DELETE'
        confirmColor='error'
        confirmText='Delete'
        data-testid={`deletePlan-${props.row.planId}`}
        isOpen={isDeleteOpen}
        message={`This will permanently delete "${props.row.description}" and all associated data. This action is irreversible.`}
        onCancel={toggleDeleteOpen}
        onConfirm={() => {
          toggleDeleteOpen();
          deletePlan.mutate({ planId: props.row.planId });
        }}
        title='Delete Plan'
      />
      {pendingSync && (
        <ConfirmationDialog
          confirmText='Sync'
          data-testid={`${pendingSync.key}-${props.row.planId}`}
          isOpen={!!pendingSync}
          message={pendingSync.message}
          onCancel={onCancelSync}
          onConfirm={onConfirmSync}
          title={pendingSync.label}
        />
      )}
    </>
  );
};
